import { ColliClient, ColliWorkflowClient } from '@frontend/colli/api';
import { Colli, ColliTriggerName } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageTriggerName, PackageType } from '@frontend/package/types';
import { PinCodeClient } from '@frontend/pin-code/api';
import { PinCode, PinCodeType } from '@frontend/pin-code/types';
import { TransactionActorClient, TransactionUserClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { ExtendedTransaction, TransactionActorType, TransactionTriggerName, TransactionUser } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    transaction: Partial<ExtendedTransaction> | null;
    pinCodes: PinCode[] | null;
    collis: Colli[] | null;
    commentsVisible: boolean;
    setCommentsVisible: (visible: boolean) => void;
    pickedUpBy: string | null;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (screen: ScreenType) => void;
    receivers: TransactionUser[] | null;
}

const useConfirmPickUp = (): ViewProps => {
    const { store, navigationState, onBack, onSubmit: onContinue, wizardId, wizardData } = useScreen();

    const [commentsVisible, setCommentsVisible] = useState<boolean>(true);
    const [receivers, setReceivers] = useState<TransactionUser[] | null>(store?.data.receivers || null);
    const [pickedUpBy, setPickedUpBy] = useState<string | null>(store?.data.pickedUpBy || null);
    const [collis, setCollis] = useState<Colli[] | null>(store?.data.collis || null);
    const [pinCodes, setPinCodes] = useState<PinCode[]>([]);

    const { packages } = wizardData;
    const { transaction, remoteTransaction, refresh } = useWizardTransaction({
        wizardId: wizardId || store!.wizard.id,
        store
    });

    useEffect(() => {
        if (transaction) {
            PinCodeClient.fetchPinCodes({ entity_id: transaction.id, entity_type: EntityType.TRANSACTION, type: PinCodeType.PICK_UP }).then((res) =>
                setPinCodes(res.results)
            );
        }
    }, [transaction]);

    useEffect(() => {
        if (!receivers && transaction)
            TransactionUserClient.fetchTransactionUsers({ transaction_id: transaction.id }).then((res) =>
                setReceivers(res.results.filter((u) => u.type === 'receiver'))
            );
        else if (store?.data.receiver) setReceivers([store.data.receiver]);
    }, [receivers, store?.data, transaction]);

    useEffect(() => {
        if (!pickedUpBy && transaction)
            TransactionActorClient.fetchTransactionActors({ transaction_id: transaction.id, type: TransactionActorType.PICKED_UP_BY }).then((res) =>
                setPickedUpBy(res.results[0]?.user_id || null)
            );
        else if (store?.data.pickedUpBy) setPickedUpBy(store.data.pickedUpBy);
    }, [pickedUpBy, store?.data, transaction]);

    useEffect(() => {
        if (!collis && transaction) ColliClient.fetchCollis({ transaction_id: transaction.id }).then((res) => setCollis(res.results));
    }, [collis, transaction]);

    const onSubmit = async () => {
        if (!store) return;

        const spot_id = navigationState.spot?.id;
        const transaction = store.data.transaction || null;
        const collis: Colli[] | null = store.data.collis || null;
        if (transaction !== null && transaction.id && transaction.account_id) {
            const colliAmount = await ColliClient.fetchCollis({ transaction_id: transaction.id })
                .then((res) => res.count)
                .catch(() => 0);
            if (!collis || colliAmount === 0 || collis.length === colliAmount) {
                await TransactionWorkflowClient.triggerTransactionState(store.data.transaction.account_id, store.data.transaction.id, {
                    trigger: TransactionTriggerName.TRANSACTION_PICKED_UP,
                    // data: { spot_id },
                    location: navigationState.spot?.id ?? undefined,
                    actor: navigationState.user?.id ?? undefined
                });
                await refresh().catch(() => {
                    // Refresh is cosmetic — failure must not block navigation
                });
            }
        }

        if (collis !== null) {
            const result = collis.map((colli) =>
                ColliWorkflowClient.triggerColliState(colli.account_id, colli.transaction_id, colli.id, {
                    trigger: ColliTriggerName.COLLI_PICKED_UP,
                    // data: { spot_id },
                    location: navigationState.spot?.id ?? undefined,
                    actor: navigationState.user?.id ?? undefined
                }).catch(() => {
                    // Individual colli failures must not block navigation
                })
            );
            await Promise.all(result);
        }

        const currentPackages: Package[] = (packages ?? []).filter(
            (pkg: Package) =>
                pkg.spot_id === spot_id && (collis == null || collis.map((c) => c.id)?.includes(pkg.colli_id ?? '')) && pkg.type === PackageType.PICK_UP
        );
        if (currentPackages?.length > 0) {
            const result = currentPackages.map((pkg) =>
                PackageWorkflowClient.triggerPackageState(pkg.account_id, pkg.transaction_id, pkg.id, {
                    trigger: PackageTriggerName.PACKAGE_PICKED_UP,
                    // data: { spot_id },
                    location: navigationState.spot?.id ?? undefined,
                    actor: navigationState.user?.id ?? undefined
                }).catch(() => {
                    // Individual package failures must not block navigation
                })
            );
            await Promise.all(result);
        }

        const nextStep = store?.steps.find(
            (step) =>
                step.screen === ScreenType.CONFIRM_PICK_UP &&
                [ScreenTrigger.SUBMIT_FALSE, ScreenTrigger.SUBMIT, ScreenTrigger.SUBMIT_TRUE].includes(step.trigger)
        );
        onContinue(nextStep);
    };

    return {
        transaction: transaction || null,
        pinCodes,
        collis,
        commentsVisible,
        setCommentsVisible,
        pickedUpBy,
        submitDisabled: !remoteTransaction,
        onSubmit,
        onBack,
        receivers
    };
};

export default useConfirmPickUp;
